/**
 * VK Votes (Голоса) — клиентский модуль оплаты
 * 
 * Путь: apps/web/src/lib/vk-votes.ts
 * 
 * Аналог Telegram Stars. Пользователь платит голосами ВК.
 * VK сам показывает окно оплаты и отправляет callback на сервер.
 * 
 * Поток:
 *   1. Клиент вызывает purchaseWithVotes(packageId)
 *   2. VK Bridge показывает VKWebAppShowOrderBox
 *   3. VK отправляет get_item callback → сервер возвращает цену
 *   4. Пользователь подтверждает → VK списывает голоса
 *   5. VK отправляет order_status_change callback → сервер начисляет игры
 *   6. Клиент получает success → обновляет UI
 */

import bridge from '@vkontakte/vk-bridge';

// ═══════════════════════════════════════════
// Пакеты игр
// Цены в голосах ВК (1 голос ≈ 7₽)
// VK показывает пользователю цену в рублях
// ═══════════════════════════════════════════

export interface VKVotesPackage {
  /** ID товара — передаётся в VKWebAppShowOrderBox и должен совпадать с серверным */
  id: string;
  /** Название для UI */
  title: string;
  /** Количество игр в пакете */
  games: number;
  /** Цена в голосах ВК */
  votes: number;
  /** Примерная цена в рублях (для отображения в UI) */
  priceRub: number;
  /** Пометка "Популярный" */
  popular?: boolean;
}

/**
 * Пакеты игр — ID должны совпадать с PACKAGES в vk-votes-routes.ts на сервере!
 */
export const VK_GAME_PACKAGES: VKVotesPackage[] = [
  {
    id: 'beshek_starter_10',
    title: '🎮 Стартовый',
    games: 10,
    votes: 21,
    priceRub: 149,
  },
  {
    id: 'beshek_popular_50',
    title: '🔥 Популярный',
    games: 50,
    votes: 85,
    priceRub: 599,
    popular: true,
  },
  {
    id: 'beshek_pro_100',
    title: '💎 Про',
    games: 100,
    votes: 143,
    priceRub: 999,
  },
  {
    id: 'beshek_ultimate_200',
    title: '🚀 Ультимейт',
    games: 200,
    votes: 243,
    priceRub: 1699,
  },
];

// ═══════════════════════════════════════════
// Покупка
// ═══════════════════════════════════════════

export interface PurchaseResult {
  success: boolean;
  orderId?: number;
  error?: string;
}

/**
 * Открыть окно покупки за голоса VK.
 * 
 * VK сам:
 * - Покажет диалог оплаты
 * - Отправит get_item callback на сервер (узнать цену)
 * - Спишет голоса у пользователя
 * - Отправит order_status_change callback (начислить игры)
 * 
 * @param packageId - ID пакета из VK_GAME_PACKAGES
 * @returns Результат покупки
 */
export async function purchaseWithVotes(packageId: string): Promise<PurchaseResult> {
  const pkg = VK_GAME_PACKAGES.find(p => p.id === packageId);
  if (!pkg) {
    return { success: false, error: 'Пакет не найден' };
  }

  try {
    const result = await bridge.send('VKWebAppShowOrderBox', {
      type: 'item',
      item: pkg.id,  // VK передаст этот item в get_item callback
    });

    if ((result as any).success) {
      // К этому моменту сервер уже получил callback и начислил игры
      return {
        success: true,
        orderId: Number(result.order_id),
      };
    } else {
      return { success: false, error: 'Платёж не завершён' };
    }
  } catch (error: any) {
    // error_code 4 = пользователь отменил
    if (error?.error_data?.error_code === 4) {
      return { success: false, error: 'cancelled' };
    }
    console.error('[VK-Votes] Purchase error:', error);
    return {
      success: false,
      error: error?.error_data?.error_reason || 'Ошибка оплаты',
    };
  }
}

/**
 * Проверить статус заказа на сервере (опционально).
 * Обычно не нужно — сервер начисляет при callback автоматически.
 * Полезно для retry-логики в UI.
 */
export async function checkOrderStatus(
  orderId: number,
  apiBase: string,
  authHeaders: Record<string, string>,
): Promise<{ success: boolean; processed: boolean }> {
  try {
    const response = await fetch(`${apiBase}/api/vk-votes/order/${orderId}`, {
      headers: authHeaders,
    });
    return await response.json();
  } catch {
    return { success: false, processed: false };
  }
}